import React, { createContext, useContext, useEffect, useMemo, useRef } from 'react'
import { ServiceState } from './helper'
import useControlPlaneServicesHealth from './useControlPlaneServicesHealth'

// Define the context type
interface ControlPlaneServicesContextType {
  serviceHealthStates: ServiceState
  servicesList: any[]
  isLoading: boolean
  reload: (showLoader?: boolean, forceReload?: boolean) => Promise<void>
  lastUpdated: number | null
}

// Create the context with default values
const ControlPlaneServicesContext = createContext<ControlPlaneServicesContextType>({
  serviceHealthStates: { total: 0, healthy: 0, unhealthy: 0, warning: 0 },
  servicesList: [],
  isLoading: false,
  reload: async () => Promise.resolve(),
  lastUpdated: null,
})

const RELOAD_INTERVAL = 10 * 60 * 1000 // 10 minutes
const CHECK_INTERVAL = 60 * 1000 // 1 minute

export const ControlPlaneServicesProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  // Get the service health data from the hook
  const {
    serviceHealthStates,
    servicesList,
    isLoading,
    reload,
    lastUpdated,
  } = useControlPlaneServicesHealth()

  // Update the service health data every 10 minutes
  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current)
    intervalRef.current = setInterval(() => {
      if (Date.now() - lastUpdated > RELOAD_INTERVAL && !isLoading) {
        reload(true, true)
      }
    }, CHECK_INTERVAL)

    return () => clearInterval(intervalRef.current)
  }, [lastUpdated, intervalRef, isLoading])

  const contextValue = useMemo(() => {
    return {
      serviceHealthStates,
      servicesList,
      isLoading,
      reload,
      lastUpdated,
    }
  }, [serviceHealthStates, servicesList, isLoading, reload, lastUpdated])

  return (
    <ControlPlaneServicesContext.Provider value={contextValue}>
      {children}
    </ControlPlaneServicesContext.Provider>
  )
}

// Custom hook to use the context
export const useControlPlaneServicesCheck = () => useContext(ControlPlaneServicesContext)

export default ControlPlaneServicesContext
